import React from "react";
import { filter, map, size } from "lodash";
import styled from "styled-components";
import { FontWeight, content } from "@glossy";
import { Locale } from "@linkfluence/models/locale";
import Tribox from "@ui-kit/form/tribox";
import GlobalSelectors from "@radarly/components/filterbar/panels/global-selectors";
import { tr } from "@radarly/i18n";
import { Choice, ClassifiedChoice, Status } from "@radarly/models/filters";

interface IProps {
  choices: Choice[];
  classifiedChoices?: ClassifiedChoice[];
  panelSize?: "s" | "m";
  onStatusChange: (choice: Choice, status: string) => void;
  onGlobalStatusChange?: (status: string, category?: string) => void;
  popularChoices?: Choice[];
  allowExclusion?: boolean;
  globalSelectors?: Status[];
  locale?: Locale;
  className?: string;
}

const Options = styled.div<{ allowExclusion?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  margin: -8px 0;
  margin-left: ${({ allowExclusion }) => allowExclusion && "-24px"};
`;

const TriboxWrapper = styled.div<{ $panelSize?: "s" | "m" }>`
  width: calc(${(props) => (props.$panelSize === "s" ? "50%" : "33%")} - 24px);
  margin: 8px 24px 8px 0;

  @media (max-width: 400px) {
    width: 100%;
  }
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 24px 0 18px 0;
`;

const WorkspaceLabel = styled.span`
  font-size: 14px;
  font-weight: ${FontWeight.Bold};
  max-width: 400px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledGlobalSelectors = styled(GlobalSelectors)`
  margin-bottom: 0;
`;

const NoData = styled.p`
  margin: 6px 0px 6px 24px;
  color: ${(props) => content(props, "secondary")};
`;

const getCheckValue = (choice: Choice) =>
  choice.status === Status.Exclude
    ? false
    : choice.status === Status.Include
    ? true
    : null;

const isIncluded = (choice: Choice) => choice.status === Status.Include;
const isExcluded = (choice: Choice) => choice.status === Status.Exclude;

const FilterbarPanelsCheckboxes = ({
  choices,
  classifiedChoices,
  panelSize = "m",
  popularChoices,
  allowExclusion = false,
  onStatusChange,
  onGlobalStatusChange,
  globalSelectors,
  locale,
  className,
}: IProps) => {
  const handleClick = (choice: Choice, checked: boolean) => {
    if (checked && !isIncluded(choice)) {
      return onStatusChange(choice, Status.Include);
    } else if (!checked && !isExcluded(choice)) {
      return onStatusChange(choice, Status.Exclude);
    }
    return onStatusChange(choice, Status.None);
  };

  const toggleClick = (choice: Choice) => {
    if (isIncluded(choice) || isExcluded(choice)) {
      return onStatusChange(choice, Status.None);
    }
    return onStatusChange(choice, Status.Include);
  };

  const classicChoices = classifiedChoices
    ? null
    : popularChoices
    ? filter(
        choices,
        (choice) => !popularChoices.some((c) => c.id === choice.id)
      )
    : choices;

  return (
    <div className={className}>
      {globalSelectors && (
        <GlobalSelectors
          onSelect={onGlobalStatusChange}
          selectors={globalSelectors}
          category={size(choices) ? choices[0].category : ""}
          locale={locale}
        />
      )}
      {popularChoices && (
        <Options allowExclusion={allowExclusion}>
          {choices &&
            map(popularChoices, (choice) => {
              return (
                <TriboxWrapper key={choice.id}>
                  <Tribox
                    label={choice.label}
                    checked={getCheckValue(choice)}
                    onClick={() => toggleClick(choice)}
                    onClickCheck={(checked) => handleClick(choice, checked)}
                    allowExclusion={allowExclusion}
                  />
                </TriboxWrapper>
              );
            })}
        </Options>
      )}
      <Options allowExclusion={allowExclusion}>
        {classicChoices &&
          map(classicChoices, (choice) => {
            return (
              <TriboxWrapper key={choice.id} $panelSize={panelSize}>
                <Tribox
                  label={choice.label}
                  checked={getCheckValue(choice)}
                  onClick={() => toggleClick(choice)}
                  onClickCheck={(checked) => handleClick(choice, checked)}
                  allowExclusion={allowExclusion}
                />
              </TriboxWrapper>
            );
          })}
      </Options>
      {classifiedChoices &&
        map(classifiedChoices, (classifiedChoice) => {
          return (
            <div key={classifiedChoice.id}>
              <Title>
                <WorkspaceLabel>{classifiedChoice.id}</WorkspaceLabel>
                {classifiedChoice.choices.length > 0 && (
                  <StyledGlobalSelectors
                    onSelect={onGlobalStatusChange}
                    selectors={["include", "exclude", "none"]}
                    category={classifiedChoice.id}
                    locale={locale}
                  />
                )}
              </Title>
              <Options allowExclusion={allowExclusion}>
                {classifiedChoice.choices.length > 0 ? (
                  map(classifiedChoice.choices, (choice) => (
                    <TriboxWrapper key={choice.id} $panelSize={panelSize}>
                      <Tribox
                        label={choice.label}
                        checked={getCheckValue(choice)}
                        onClick={() => toggleClick(choice)}
                        onClickCheck={(checked) => handleClick(choice, checked)}
                        allowExclusion={allowExclusion}
                      />
                    </TriboxWrapper>
                  ))
                ) : (
                  <NoData>{tr(locale, "DASHBOARD_HOME::FOCUSES_EMPTY")}</NoData>
                )}
              </Options>
            </div>
          );
        })}
    </div>
  );
};

export default FilterbarPanelsCheckboxes;
